import type { FeatureKey } from "@/lib/authz/policy";
import type { PlanTier } from "@/lib/authz/plans";
import { MATRIX_TIERS } from "@/components/pricing/pricingMatrix";

/**
 * Which plan a FREE-tool visitor should be pointed at for a given paid
 * FeatureKey — the cheapest currently-marketed tier that actually unlocks it.
 *
 * Client-safe on purpose: this feeds `FreeToolUpsell`, which renders on
 * statically-prerendered `/free/**` pages. Do not import anything from
 * `@/lib/server/**` here.
 *
 * SOURCE OF TRUTH AND WHY THIS IS HAND-WRITTEN RATHER THAN DERIVED
 *
 * The real entitlement data lives server-side, in
 * `src/lib/server/sealSubscriptions.ts` (`FEATURES_BY_VARIANT_ID`, and the
 * `ALL_VEHICLE_DEVELOPER_APPS` / `ALL_SERVER_DEVELOPER_APPS` arrays that feed
 * `FEATURES_BY_TITLE` — the title-based map is what actually grants access
 * today, per that file's own comment: Seal returns no variant_id for any live
 * subscription). This file cannot import that module, so the mapping below is
 * a hand-copied snapshot of it. `featurePlans.test.ts` (which runs in a node
 * environment and may import server modules) asserts this snapshot agrees
 * with the real arrays and covers every `FeatureKey`, so drift between the two
 * fails a test instead of shipping a wrong plan or price on a live page.
 *
 * `vehicle_dev` and `server_dev` are parallel tracks (see the lattice comment
 * in `@/lib/authz/plans`) — neither is a subset of the other — so a feature
 * present in both bundles (e.g. `metaroom`, `creatorconvert`) resolves to
 * whichever of the two is cheaper (`vehicle_dev`, $14.99 vs $19.99).
 *
 * Three features are not in either developer bundle array and are hardcoded here
 * for a documented reason rather than derived:
 *  - `blinklab3d` is granted only by Studio. Its eligible tier is `studio`.
 *  - `badgemaker` piggybacks on `itemimage` access at the policy layer
 *    (`buildFeaturePermissions` in `@/lib/authz/policy`: "badgemaker:
 *    personalFeatures.has('badgemaker') || personalFeatures.has('itemimage')"),
 *    so it is granted to every `itemimage` (server_dev) subscriber even though
 *    no bundle array lists it by name.
 *  - `inklab` is admin-gated and, per that same file's comment, "mapped to NO
 *    plan bundle" — nobody can buy it yet. It falls back to `all_services`
 *    (Pro) as the nominal "everything" tier; update this the day it ships to
 *    an actual bundle.
 */
const FEATURE_ENTRY_PLAN: Record<FeatureKey, PlanTier> = {
  // Vehicle Developer bundle ($14.99) — cheapest tier for every vehicle-track tool.
  paintbooth: "vehicle_dev",
  liverymagic: "vehicle_dev",
  uvstudio: "vehicle_dev",
  blinklab: "vehicle_dev",
  chevronbuilder: "vehicle_dev",
  handlingdiff: "vehicle_dev",
  // In both bundles; vehicle_dev is the cheaper of the two.
  metaroom: "vehicle_dev",
  creatorconvert: "vehicle_dev",
  assets: "vehicle_dev",

  // Server Developer bundle ($19.99) — vehicle-track-exclusive-free tools.
  packbench: "server_dev",
  codesmith: "server_dev",
  codevisualizer: "server_dev",
  scriptdocs: "server_dev",
  eupviewer: "server_dev",
  addoneupcreator: "server_dev",
  weaponviewer: "server_dev",
  itemimage: "server_dev",
  embroiderytool: "server_dev",
  tripoai: "server_dev",
  resmon: "server_dev",
  // Not in any bundle array; piggybacks on `itemimage` (server_dev) — see the
  // file header above.
  badgemaker: "server_dev",

  // 3D BlinkLab requires Studio.
  blinklab3d: "studio",

  // Not sold on any plan yet (admin-gated) — nominal Pro fallback.
  inklab: "all_services",
};

/** The cheapest PlanTier whose toolset unlocks `feature`. */
export function entryPlanForFeature(feature: FeatureKey): PlanTier {
  return FEATURE_ENTRY_PLAN[feature];
}

/** `entryPlanForFeature`, joined against the pricing matrix for display. */
export function planOfferForFeature(feature: FeatureKey): {
  tier: PlanTier;
  name: string;
  price: string;
} {
  const tier = entryPlanForFeature(feature);
  const matrixTier = MATRIX_TIERS.find((t) => t.key === tier);
  if (!matrixTier) {
    throw new Error(
      `featurePlans: no pricing-matrix entry for tier "${tier}" (feature "${feature}")`,
    );
  }
  return { tier, name: matrixTier.name, price: matrixTier.price };
}
